import {
  PasswordPatternRequirement,
  useClientDetailRequest,
  useLocalize,
  useLoginRequest,
  useRegisterRequest,
} from "@/app/api"
import {
  changeBackUrl,
  changeClientDetail,
  changeCode,
  changeEmail,
  changeMode,
  changeRetryInSeconds,
  changeCountryCode,
  changePhone,
  changeOtpTarget,
  changePassword,
} from "@/app/appSlice"
import { RootState } from "@/app/store"
import { currentTheme } from "@/theme"
import sanitizeHtml from "sanitize-html"

export default function Login() {
  let [searchParams, setSearchParams] = useSearchParams()
  let nav = useNavigate()
  let t = useTranslate()
  let localize = useLocalize()
  let dispatch = useAppDispatch()
  // const detailEnq = useDetailEnquiry()
  const login = useLoginRequest()
  const register = useRegisterRequest()
  const clientDetailRequest = useClientDetailRequest()

  let [countryCode, setCountryCode] = useState("852")
  let [code, setCode] = useState("")
  let [email, setEmail] = useState("")
  let [phone, setPhone] = useState("")
  let [errMsg, setErrmsg] = useState("")
  let [registered, setRegistered] = useState(true)
  let [password, setPassword] = useState("")
  let [confirmPassword, setConfirmPassword] = useState("")
  let [showPassword, setShowPassword] = useState(false)
  let [showConfirmPassword, setShowConfirmPassword] = useState(false)
  let [passwordOk, setPasswordOk] = useState(false)
  let [passwordErrMsg, setPasswordErrMsg] = useState("")
  const clientDetail = useAppSelector(
    (state: RootState) => state.app.clientDetail,
  )
  const backUrl = useAppSelector((state: RootState) => state.app.backUrl)

  useEffect(() => {
    ;(async () => {
      const _code = searchParams.get("code")
      const returnUrl = searchParams.get("returnUrl")
      if (!_code || !returnUrl) {
        nav("/error")
        return
      }
      setCode(_code)
      dispatch(changeBackUrl(decodeURIComponent(returnUrl)))
      dispatch(changeCode(_code))
      const res = await clientDetailRequest(_code)
      dispatch(changeClientDetail(res))
    })()
  }, [])

  useEffect(() => {
    setPasswordErrMsg("")
    setPasswordOk(false)
    if (password.length === 0) {
      return
    }

    if (password.length < clientDetail.passwordPatternRequirement.minLength) {
      setPasswordErrMsg("password_too_short")
      return
    }

    if (
      clientDetail.passwordPatternRequirement.upperCase &&
      !/[A-Z]/.test(password)
    ) {
      setPasswordErrMsg("password_missing_upper")
      return
    }

    if (
      clientDetail.passwordPatternRequirement.lowerCase &&
      !/[a-z]/.test(password)
    ) {
      setPasswordErrMsg("password_missing_lower")
      return
    }

    if (
      clientDetail.passwordPatternRequirement.number &&
      !/[0-9]/.test(password)
    ) {
      setPasswordErrMsg("password_missing_number")
      return
    }

    if (
      clientDetail.passwordPatternRequirement.specialCharacter &&
      !/[\!\@\#\$\%\^\&\*\-\_\=\+\{\}\[\]\|\:\;\'\"\<\>\,\.\?\/\`\~]/.test(
        password,
      )
    ) {
      setPasswordErrMsg("password_missing_special")
      return
    }

    if (password !== confirmPassword) {
      setPasswordErrMsg("password_not_match")
      return
    }

    setPasswordOk(true)
  }, [password, confirmPassword])

  const submit = async (_code = "") => {
    setErrmsg("")
    const res = await login(phone ? countryCode + phone : "", email, code)
    if (!res.success) {
      setErrmsg(res.error)
      if (res.error === "user_not_found") {
        if (currentTheme.type === "lubuds") {
          setErrmsg("lubuds_user_not_found")
        }
        setRegistered(false)
      }
      return
    }
    dispatch(changeEmail(email))
    dispatch(changeRetryInSeconds(res.retryInSeconds))
    dispatch(changeMode("sign_in"))
    dispatch(changePhone(phone))
    dispatch(changeCountryCode(countryCode))
    dispatch(changeOtpTarget(res.otpTarget))
    nav("/otp")
  }

  const goRegister = async () => {
    setPasswordErrMsg("")

    if (password !== confirmPassword) {
      setPasswordErrMsg(t("password_not_match"))
      return
    }

    const res = await register(
      phone ? countryCode + phone : "",
      email,
      code,
      password,
    )
    if (!res.success) {
      setPasswordErrMsg(res.error)
      return
    }
    dispatch(changeEmail(email))
    dispatch(changeRetryInSeconds(res.retryInSeconds))
    dispatch(changeMode("register"))
    dispatch(changePhone(phone))
    dispatch(changeCountryCode(countryCode))
    dispatch(changeOtpTarget(res.otpTarget))
    dispatch(changePassword(password))
    nav("/otp")
  }

  const goRedirectBack = () => {
    console.log(backUrl)
    window.location.href = backUrl
  }

  const getPasswordInputLabelParams = (
    passwordPatternRequirement: PasswordPatternRequirement,
  ) => {
    const including = []
    if (passwordPatternRequirement.upperCase) {
      including.push(t("password_include_upper"))
    }
    if (passwordPatternRequirement.lowerCase) {
      including.push(t("password_include_lower"))
    }
    if (passwordPatternRequirement.number) {
      including.push(t("password_include_number"))
    }
    if (passwordPatternRequirement.specialCharacter) {
      including.push(t("password_include_special"))
    }

    let includingString = including.join("")
    if (includingString.length > 0) {
      includingString = t("password_input_label_including", {
        including: includingString,
      })
    }

    return {
      minLength: passwordPatternRequirement.minLength,
      including: includingString,
    }
  }

  return (
    <div>
      <div className="text-2xl font-bold mt-5">{t("sign_in_header")}</div>
      <div className="mt-2 mb-10">
        {t("sign_in_header_sub", { name: localize(clientDetail, "name") })}
      </div>

      {clientDetail && clientDetail.loginMethods.includes("email") && (
        <>
          <div className="text-left text-sm">{t("email_input_label")}</div>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={clsx([
              { "border-red-500": !!errMsg },
              "w-full mt-2 p-2",
            ])}
            placeholder={t("email_input_placeholder")}
          />
        </>
      )}

      {clientDetail && clientDetail.loginMethods.includes("phone") && (
        <>
          <div className="text-left text-sm mt-4">{t("phone_input_label")}</div>
          <div className="mt-2 flex justify-start">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className={clsx([
                { "border-red-500": !!errMsg },
                "p-2 w-20 mr-2",
              ])}
            >
              {clientDetail.countryCodes.map((countryCode) => (
                <option
                  key={countryCode}
                  value={countryCode}
                  selected={countryCode == "852"}
                >{`+${countryCode}`}</option>
              ))}
            </select>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={clsx([
                { "border-red-500": !!errMsg },
                "p-2 flex-grow",
              ])}
              placeholder={t("phone_input_placeholder")}
              readOnly={!registered}
            />
          </div>
        </>
      )}

      {errMsg && (
        <div className="text-red-500 mt-1 text-left text-xs">
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(t(errMsg), {
                allowedTags: ["a"],
                allowedAttributes: {
                  a: ["href", "class"],
                },
                transformTags: {
                  a: sanitizeHtml.simpleTransform("a", {
                    class: "color-primary",
                  }),
                },
              }),
            }}
          />
        </div>
      )}
      <div className="mt-10">
        <button
          className={`w-full m-0`}
          onClick={async () => {
            submit()
          }}
          disabled={(!!!email && !!!phone) || !registered}
        >
          {t("sign_in")}
        </button>
      </div>

      {!registered && (
        <div className="mt-10">
          <div
            className="text-left text-sm mt-4"
            style={{ whiteSpace: "pre-line" }}
          >
            {t(
              "password_input_label",
              getPasswordInputLabelParams(
                clientDetail.passwordPatternRequirement,
              ),
            )}
          </div>
          <div className="mt-2 flex justify-start">
            <div className="relative flex-grow">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={clsx([
                  { "border-red-500": !!passwordErrMsg },
                  "p-2 w-full",
                ])}
                placeholder={t("password_input_placeholder")}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <img
                    src="/show_password_icon.svg"
                    alt="eye"
                    className="w-5 h-5"
                  />
                ) : (
                  <img
                    src="/hide_password_icon.svg"
                    alt="eye"
                    className="w-5 h-5"
                  />
                )}
              </button>
            </div>
          </div>
          <div className="mt-2 flex justify-start">
            <div className="relative flex-grow">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={clsx([
                  { "border-red-500": !!passwordErrMsg },
                  "p-2 w-full",
                ])}
                placeholder={t("password_confirm_placeholder")}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <img
                    src="/show_password_icon.svg"
                    alt="eye"
                    className="w-5 h-5"
                  />
                ) : (
                  <img
                    src="/hide_password_icon.svg"
                    alt="eye"
                    className="w-5 h-5"
                  />
                )}
              </button>
            </div>
          </div>
          {passwordErrMsg && (
            <div className="text-red-500 mt-1 text-left text-xs">
              {t(passwordErrMsg)}
            </div>
          )}
          <div className="mt-2 flex justify-start">
            <button
              className={`w-full m-0 mt-4`}
              onClick={() => goRegister()}
              disabled={!passwordOk}
            >
              {t("register")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
